import { DEFAULT_TAKE } from "@react3l/react3l/config";
import { Model, ModelFilter } from "@react3l/react3l/core";
import { Modal } from "antd";
import { PaginationProps } from "antd/lib/pagination";
import {
  RowSelectionType,
  SortOrder,
  TableRowSelection,
} from "antd/lib/table/interface";
import {
  Dispatch,
  Reducer,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { Observable } from "rxjs";
import listService from "services/list-service";

type KeyType = string | number;

export interface ModalState {
  visible?: boolean;
  loadControl?: boolean;
}

export interface ModalAction {
  type: ModalActionEnum;
}

export interface ContentTableState<
  TMapping extends Model,
  TMapper extends Model = any
> {
  mappingList?: TMapping[]; // for content table
  mapperList?: TMapper[]; // for content modal table
}

export interface ContentTableAction<
  TMapping extends Model,
  TMapper extends Model = any
> {
  type: ContentTableActionEnum;
  listName?: string;
  mappingList?: TMapping[];
  mapperList?: TMapper[];
  data?: any[];
}

export enum ModalActionEnum {
  OPEN_MODAL,
  CLOSE_MODAL,
  END_LOAD_CONTROL,
  INIT_SEARCH,
}

export enum ContentTableActionEnum {
  SET_LIST_SELECTION,
  SINGLE_DELETE,
  BULK_DELETE,
}

function modalTableReducer(state: ModalState, action: ModalAction) {
  switch (action.type) {
    case ModalActionEnum.OPEN_MODAL: {
      return {
        ...state,
        visible: true,
        loadControl: true,
      };
    }
    case ModalActionEnum.CLOSE_MODAL: {
      return {
        ...state,
        visible: false,
        loadControl: false,
      };
    }
    case ModalActionEnum.END_LOAD_CONTROL: {
      return {
        ...state,
        loadControl: false,
      };
    }
    case ModalActionEnum.INIT_SEARCH: {
      return {
        ...state,
        loadControl: true,
      };
    }
  }
}

function contentTableReducer<TMapping extends Model, TMapper extends Model>(
  state: ContentTableState<TMapping, TMapper>,
  action: ContentTableAction<TMapping, TMapper>
) {
  switch (action.type) {
    case ContentTableActionEnum.SINGLE_DELETE: {
      return {
        ...state,
        mappingList: action.mappingList,
      };
    }
    case ContentTableActionEnum.BULK_DELETE: {
      return {
        ...state,
        mappingList: [],
      };
    }
    case ContentTableActionEnum.SET_LIST_SELECTION: {
      return {
        ...state,
        [action.listName]: action.data,
      };
    }
  }
}

/* services to CRUD, import, export data in table */
const useRowSelection = (
  selectionType: RowSelectionType = "checkbox",
  derivedRowKeys?: KeyType[]
) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<KeyType[]>(
    derivedRowKeys ?? []
  );

  const canBulkDelete = useMemo(() => selectedRowKeys.length > 0, [
    selectedRowKeys.length,
  ]);

  return {
    rowSelection: useMemo(
      () => ({
        onChange(newSelectedRowKeys: KeyType[]) {
          setSelectedRowKeys(newSelectedRowKeys);
        },
        type: selectionType,
      }),
      [selectionType]
    ),
    selectedRowKeys,
    setSelectedRowKeys,
    canBulkDelete,
  };
};

const useContentRowSelection = <T extends Model>(
  selectedList: T[],
  setSelectedList: (t: T[]) => void,
  disabled: boolean = false,
  selectionType: RowSelectionType = "checkbox"
): {
  rowSelection: TableRowSelection<T>;
  canBulkDelete: boolean;
} => {
  const selectedRowKeys: KeyType[] = useMemo(() => {
    return selectedList.length > 0
      ? selectedList.map((t: T) => (t?.id ? t?.id : t?.key))
      : [];
  }, [selectedList]);

  const canBulkDelete = useMemo(() => selectedRowKeys.length > 0, [
    selectedRowKeys.length,
  ]);

  return {
    rowSelection: useMemo(
      () => ({
        preserveSelectedRowKeys: true,
        onSelect: (record: T, selected: boolean) => {
          if (selected) {
            selectedList.push(record);
            setSelectedList([...selectedList]);
          } else {
            setSelectedList(
              selectedList.filter((t: T) => {
                return t.id ? t.id !== record.id : t.key !== record.key;
              })
            );
          }
        },
        onChange: (...[nextSelectedRowKeys, selectedRows]) => {
          if (selectedList?.length === 0) {
            setSelectedList([...selectedRows]);
            return;
          }
          const mapper: Record<any, number> = {};
          nextSelectedRowKeys.forEach((key: string | number) => {
            mapper[key] = 0;
          });
          const mergeList = [...selectedList, ...selectedRows].filter(
            (item) => typeof item !== "undefined"
          );
          const filterList: T[] = [];
          mergeList.forEach((item) => {
            const itemId = typeof item?.id !== "undefined" ? item.id : item.key;
            if (
              Object.prototype.hasOwnProperty.call(mapper, itemId) &&
              mapper[itemId] === 0
            ) {
              filterList.push(item);
              mapper[itemId] = mapper[itemId] + 1;
            }
          });
          setSelectedList([...filterList]);
        },
        getCheckboxProps: () => ({
          disabled,
        }),
        type: selectionType,
        selectedRowKeys,
      }),
      [disabled, selectedList, selectedRowKeys, selectionType, setSelectedList]
    ),
    canBulkDelete,
  };
};

const usePagination = <TFilter extends ModelFilter>(
  filter: TFilter,
  total: number
): PaginationProps => {
  return useMemo(
    () => ({
      current: Math.ceil(filter.skip / filter.take) + 1,
      pageSize: filter.take,
      total,
    }),
    [filter.skip, filter.take, total]
  );
};

const useTableChange = <TFilter extends ModelFilter>(
  filter: TFilter,
  setFilter: (filter: TFilter) => void,
  handleSearch?: () => void
) => {
  const handleTableChange = useCallback(
    (...[, , sorter]) => {
      let newFilter = { ...filter };
      if (
        sorter.field !== filter.orderBy ||
        sorter.order !== getAntOrderType(filter, sorter.field)
      ) {
        newFilter = {
          ...newFilter,
          orderBy: sorter.field,
          orderType: getOrderType(sorter.order),
        };
      }
      setFilter({ ...newFilter });
      if (typeof handleSearch === "function") {
        handleSearch();
      }
    },
    [filter, setFilter, handleSearch]
  );

  const handlePagination = useCallback(
    (skip: number, take: number) => {
      setFilter({ ...filter, skip, take });
      if (typeof handleSearch === "function") {
        handleSearch();
      }
    },
    [filter, setFilter, handleSearch]
  );

  return { handleTableChange, handlePagination };
};

const useTable = <T extends Model, TFilter extends ModelFilter>(
  filter: TFilter,
  setFilter: (filter: TFilter) => void,
  handleSearch,
  selectedRowKeys,
  onServerDelete,
  onServerBulkDelete
) => {
  const { handleTableChange, handlePagination } = useTableChange<TFilter>(
    filter,
    setFilter,
    handleSearch
  );

  const handleServerDelete = useCallback(
    (item: T) => {
      Modal.confirm({
        title: "Bạn có chắc chắn muốn xóa?",
        content: "Thao tác không thể khôi phục",
        okText: "Xác nhận",
        cancelText: "Hủy",
        okType: "danger",
        onOk() {
          onServerDelete(item);
        },
      });
    },
    [onServerDelete]
  );

  const handleServerBulkDelete = useCallback(() => {
    Modal.confirm({
      title: "Bạn có chắc chắn muốn xóa?",
      content: "Thao tác không thể khôi phục",
      okText: "Xác nhận",
      cancelText: "Hủy",
      okType: "danger",
      onOk() {
        onServerBulkDelete(selectedRowKeys as number[]);
      },
    });
  }, [onServerBulkDelete, selectedRowKeys]);

  return {
    handleTableChange,
    handlePagination,
    handleServerDelete,
    handleServerBulkDelete,
  };
};

const useModalTable = <T extends Model, TFilter extends ModelFilter>(
  filter: TFilter,
  setFilter: (filter: TFilter) => void,
  loadList: boolean,
  setLoadList: Dispatch<SetStateAction<boolean>>,
  handleSearch: () => void,
  getList: (filter: TFilter) => Observable<T[]>,
  getTotal: (filter: TFilter) => Observable<number>,
  mapperList?: T[]
) => {
  const [{ mapperList: selectedList }, dispatch] = useReducer<
    Reducer<ContentTableState<T>, ContentTableAction<T>>
  >(contentTableReducer, {
    mapperList: mapperList ?? [],
  });
  const setSelectedList = useCallback((list: T[]) => {
    dispatch({
      type: ContentTableActionEnum.SET_LIST_SELECTION,
      listName: "mapperList",
      data: list,
    });
  }, []);

  const { rowSelection } = useContentRowSelection(
    selectedList,
    setSelectedList
  );

  const setMapperList = useCallback((nextMapperList: T[]) => {
    dispatch({
      type: ContentTableActionEnum.SET_LIST_SELECTION,
      listName: "mapperList",
      data: nextMapperList,
    });
  }, []);

  useEffect(() => {
    if (mapperList) {
      setMapperList(mapperList);
    }
  }, [mapperList, setMapperList]);

  const { list, total, loadingList } = listService.useList(
    filter,
    setFilter,
    loadList,
    setLoadList,
    handleSearch,
    getList,
    getTotal,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined
  );

  const pagination: PaginationProps = usePagination<TFilter>(filter, total);

  const { handleTableChange, handlePagination } = useTableChange<TFilter>(
    filter,
    setFilter,
    handleSearch
  );

  return {
    list,
    total,
    loadingList,
    pagination,
    handleTableChange,
    handlePagination,
    rowSelection,
    selectedList,
    setSelectedList,
  };
};

const useLocalTable = <
  T extends Model,
  T2 extends Model,
  TFilter extends ModelFilter
>(
  filter: TFilter,
  setFilter: (filter: TFilter) => void,
  setLoadList: Dispatch<SetStateAction<boolean>>,
  handleSearch: () => void,
  total: number,
  source: T[],
  setSource: (source: T[]) => void,
  ContentClass: new () => T
) => {
  const [{ mappingList }, dispatch] = useReducer<
    Reducer<ContentTableState<T, T2>, ContentTableAction<T, T2>>
  >(contentTableReducer, {
    mappingList: [],
  });

  const setMappingList = useCallback((nextMappingList: T[]) => {
    dispatch({
      type: ContentTableActionEnum.SET_LIST_SELECTION,
      listName: "mappingList",
      data: nextMappingList,
    });
  }, []);

  const { rowSelection, canBulkDelete } = useContentRowSelection(
    mappingList,
    setMappingList
  );

  const pagination: PaginationProps = usePagination<TFilter>(filter, total);

  const { handleTableChange, handlePagination } = useTableChange<TFilter>(
    filter,
    setFilter,
    handleSearch
  );
  const resetTableFilter = useCallback(() => {
    setFilter({ ...filter, skip: 0, take: DEFAULT_TAKE });
    handleSearch();
  }, [filter, handleSearch, setFilter]);

  const handleLocalDelete = useCallback(
    (content: T) => {
      setSource(source.filter(filterContent(content)));
      dispatch({
        type: ContentTableActionEnum.SINGLE_DELETE,
        mappingList: mappingList.filter(filterContent(content)),
      });
      resetTableFilter();
    },
    [mappingList, resetTableFilter, setSource, source]
  );

  const handleLocalBulkDelete = useCallback(() => {
    Modal.confirm({
      title: "Bạn có chắc chắn muốn xóa?",
      content: "Thao tác không thể khôi phục",
      okText: "Xác nhận",
      cancelText: "Hủy",
      okType: "danger",
      onOk() {
        setSource(
          source.filter(
            filterContentNotInList(getIdsFromContent(mappingList, "key"), "key")
          )
        );
        dispatch({
          type: ContentTableActionEnum.BULK_DELETE,
        });
        resetTableFilter();
      },
    });
  }, [mappingList, resetTableFilter, setSource, source]);

  const handleChangeOneCell = useCallback(
    (index: number, field: keyof T) => (value: T[keyof T], object?: any) => {
      if (index !== -1) {
        if (typeof object === "object") {
          source[index][field] = object;
          source[index][`${String(field)}Id` as keyof T] = value;
        } else {
          source[index][field] = value;
        }
      }
      setSource(source);
      resetTableFilter();
    },
    [setSource, source, resetTableFilter]
  );

  const handleChangeOneRow = useCallback(
    (key: string) => (value: T) => {
      const index = source.findIndex((item) => item.key === key);
      source[index] = value;
      setSource(source);
      resetTableFilter();
    },
    [source, resetTableFilter, setSource]
  );

  const handleAddContent = useCallback(() => {
    source?.length > 0
      ? setSource([...source, new ContentClass()])
      : setSource([new ContentClass()]);
    setLoadList(true);
  }, [ContentClass, setLoadList, setSource, source]);

  return {
    handleSearch,
    pagination,
    handleTableChange,
    handlePagination,
    rowSelection,
    canBulkDelete,
    selectedContent: mappingList,
    resetTableFilter,
    handleLocalDelete,
    handleLocalBulkDelete,
    handleChangeOneCell,
    handleChangeOneRow,
    handleAddContent,
  };
};

const useContenModal = (handleSource?: () => void) => {
  const [{ visible, loadControl }, dispatch] = useReducer<
    Reducer<ModalState, ModalAction>
  >(modalTableReducer, {
    visible: false,
    loadControl: false,
  });

  const handleOpenModal = useCallback(() => {
    dispatch({ type: ModalActionEnum.OPEN_MODAL });
  }, []);

  const handleCloseModal = useCallback(() => {
    dispatch({ type: ModalActionEnum.CLOSE_MODAL });
  }, []);

  const handleSaveModal = useCallback(() => {
    dispatch({ type: ModalActionEnum.CLOSE_MODAL });
    if (typeof handleSource === "function") {
      handleSource();
    }
  }, [handleSource]);

  const handleEndControl = useCallback(() => {
    dispatch({ type: ModalActionEnum.END_LOAD_CONTROL });
  }, []);

  const handleSearchModal = useCallback(() => {
    dispatch({ type: ModalActionEnum.INIT_SEARCH });
  }, []);

  return {
    visible,
    loadControl,
    handleEndControl,
    handleOpenModal,
    handleCloseModal,
    handleSaveModal,
    handleSearchModal,
  };
};

const tableService = {
  useRowSelection,
  useContentRowSelection,
  usePagination,
  useTableChange,
  useTable,
  useModalTable,
  useLocalTable,
  useContenModal,
};

export function getAntOrderType<T extends Model, TFilter extends ModelFilter>(
  tFilter: TFilter,
  columnName: keyof T
): SortOrder {
  if (tFilter.orderBy === columnName) {
    switch (tFilter.orderType) {
      case "ASC":
        return "ascend";

      case "DESC":
        return "descend";

      default:
        return undefined;
    }
  }
  return undefined;
}

export function getOrderType(sortOrder?: SortOrder) {
  switch (sortOrder) {
    case "ascend":
      return "ASC";

    case "descend":
      return "DESC";

    default:
      return undefined;
  }
}

export function filterContent<T extends Model>(content: T) {
  return (item: T) => item.key !== content.key;
}

export function getIdsFromContent<T extends Model>(list: T[], fieldId: string) {
  return list.map((item) => item[fieldId]);
}

export function filterContentNotInList<T extends Model>(
  list: any[],
  fieldId: string
) {
  return (item: T) => !list.includes(item[fieldId]);
}

export function filterContentInList<T extends Model>(
  list: any[],
  fieldId: string
) {
  return (item: T) => list.includes(item[fieldId]);
}

export function mappingToMapper<T extends Model>(mapperField: string) {
  return (item: T) => item[mapperField];
}

export default tableService;
